/**
 * SuggestReassignmentDialog — drill-down + reassign for a single
 * Team-Availability cell (one resource × one week).
 *
 * Open via clicking any non-zero cell on the calendar. Loads
 * `/api/team-availability/cell-detail?resource_id=...&week_iso=...`
 * once, then performs **local** simulation of every change so the
 * admin can see the impact on themselves AND on each peer in real
 * time before committing. Save fans out one
 * `PUT /api/estimates/{id}/resource-assignments` per modified estimate.
 *
 * Math: hours_for(resource_X, estimate_E) =
 *   fte_E_this_week × cap_X × (pct_X / 100)
 * where pct_X is either the explicit pct or (residual_pct / N_eligible).
 */
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Switch } from './ui/switch';
import { toast } from 'sonner';
import {
  Loader2, AlertTriangle, ArrowRight, ChevronDown, Save, RotateCcw,
  TrendingDown, TrendingUp, ExternalLink, Sparkles, X,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const loadColor = (ratio) => {
  if (ratio <= 0.05) return { chip: 'bg-slate-100 text-slate-600', text: 'text-slate-600' };
  if (ratio <= 0.25) return { chip: 'bg-emerald-50 text-emerald-700', text: 'text-emerald-700' };
  if (ratio <= 0.80) return { chip: 'bg-emerald-200 text-emerald-900', text: 'text-emerald-800' };
  if (ratio <= 1.00) return { chip: 'bg-amber-200 text-amber-900', text: 'text-amber-900' };
  if (ratio <= 1.5)  return { chip: 'bg-rose-200 text-rose-900', text: 'text-rose-800' };
  return { chip: 'bg-rose-500 text-white', text: 'text-rose-700' };
};

const pctLabel = (n) => `${Math.round(n * 100)}%`;

/**
 * For a given estimate (contribution), apply the user's overrides
 * (selfExplicitPct, addedAssignments) and return:
 *   { newExplicitMap, newResidualPct, newResidualPeerPct, newEligibleResidualIds }
 * This is the shared math used in every preview row.
 */
function simulateEstimate(contribution, overrides, peers, explicitOnly = false) {
  // Start from current assignments.
  const explicit = {};
  for (const a of contribution.current_assignments || []) {
    explicit[a.resource_id] = a.explicit_pct;
  }
  // Apply self override. ANY value (including 0) means "explicit at that
  // value" — keeps self OUT of the residual pool. To put self back into
  // residual the admin must hit "Reset" on this estimate row.
  const selfId = overrides.selfId;
  if (overrides.selfExplicitPct != null) {
    explicit[selfId] = overrides.selfExplicitPct;
  }
  // Apply added peer assignments. Same semantic — keep peer explicit.
  for (const ap of overrides.addedAssignments || []) {
    explicit[ap.resource_id] = ap.pct;
  }
  const explicitTotal = Object.values(explicit).reduce((a, b) => a + b, 0);
  // In explicit-only mode we ignore the divide-equally residual entirely,
  // matching the Portfolio Heat-map (explicit assignments only).
  const residualPct = explicitOnly ? 0 : Math.max(0, 100 - explicitTotal);
  // Eligible residual peers = active peers, not blocked, not in explicit map.
  const eligible = explicitOnly ? [] : peers
    .filter(p => !p.blocked_this_week && !(p.resource_id in explicit));
  const perPeerPct = (eligible.length > 0 && residualPct > 0) ? residualPct / eligible.length : 0;
  return { explicit, residualPct, perPeerPct, eligibleIds: eligible.map(p => p.resource_id) };
}

/** Compute peer's new hours for this week given the simulated states. */
function peerHoursAfter(contributions, peer, overridesByEst, peers, explicitOnly = false) {
  if (peer.blocked_this_week) return 0;
  let total = 0;
  for (const c of contributions) {
    const fte = c.fte_this_week;
    const o = overridesByEst[c.estimate_id] || { selfId: peer.resource_id };
    const sim = simulateEstimate(c, o, peers, explicitOnly);
    let pct = 0;
    if (peer.resource_id in sim.explicit) {
      pct = sim.explicit[peer.resource_id];
    } else if (sim.eligibleIds.includes(peer.resource_id)) {
      pct = sim.perPeerPct;
    }
    total += fte * peer.capacity * (pct / 100);
  }
  return total;
}

export default function SuggestReassignmentDialog({ open, onClose, resourceId, weekIso, onSaved, defaultExplicitOnly = false }) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [overrides, setOverrides] = useState({}); // { [estimate_id]: { selfId, selfExplicitPct, addedAssignments } }
  const [saving, setSaving] = useState(false);
  const [showAllContribs, setShowAllContribs] = useState(false);
  const [explicitOnly, setExplicitOnly] = useState(defaultExplicitOnly);

  useEffect(() => {
    if (open) setExplicitOnly(defaultExplicitOnly);
  }, [open, defaultExplicitOnly]);

  useEffect(() => {
    if (!open || !resourceId || !weekIso) return;
    setLoading(true);
    setError(null);
    setData(null);
    setOverrides({});
    axios.get(
      `${API_URL}/api/team-availability/cell-detail?resource_id=${resourceId}&week_iso=${weekIso}&explicit_only=${explicitOnly}`,
      { withCredentials: true },
    )
      .then(r => setData(r.data))
      .catch(err => setError(err?.response?.data?.detail || err?.message || 'Failed to load'))
      .finally(() => setLoading(false));
  }, [open, resourceId, weekIso, explicitOnly]);

  const peers = data?.peers_in_role || [];
  const contributions = data?.contributions || [];
  const selfId = data?.resource?.id;

  // Sort contributions by self_hours desc — the biggest offenders surface first.
  const sortedContribs = useMemo(
    () => [...contributions].sort((a, b) => b.self_hours_this_week - a.self_hours_this_week),
    [contributions],
  );
  const visibleContribs = showAllContribs ? sortedContribs : sortedContribs.slice(0, 6);

  // Suggested peers: lightest 2 same-role peers, not blocked, not self.
  const suggestedPeers = useMemo(() => {
    return peers
      .filter(p => !p.is_self && !p.blocked_this_week)
      .slice(0, 2);
  }, [peers]);

  // Build a uniform overridesByEst for peer-impact calc.
  const overridesByEst = useMemo(() => {
    const out = {};
    for (const c of sortedContribs) {
      out[c.estimate_id] = overrides[c.estimate_id] || { selfId };
    }
    return out;
  }, [sortedContribs, overrides, selfId]);

  // Impact preview rows: self + every peer with current OR projected hours > 0.
  const impactRows = useMemo(() => {
    if (!data) return [];
    return peers.map(p => {
      const after = peerHoursAfter(sortedContribs, p, overridesByEst, peers, explicitOnly);
      // For weeks not in scope, "current" already reflects all estimates.
      // We only re-simulate within the contributing estimates (those that
      // touched THIS resource), so the delta = sum(after - before) limited
      // to those. Anything outside stays untouched.
      // Compute "before" within contributions only:
      let before = 0;
      for (const c of sortedContribs) {
        const sim = simulateEstimate(c, { selfId: p.resource_id }, peers, explicitOnly);
        let pct = 0;
        if (p.resource_id in sim.explicit) pct = sim.explicit[p.resource_id];
        else if (sim.eligibleIds.includes(p.resource_id)) pct = sim.perPeerPct;
        before += c.fte_this_week * p.capacity * (pct / 100);
      }
      const baseline = p.current_hours_this_week; // includes all estimates (in + out of scope)
      const delta = after - before;
      const projected = baseline + delta;
      return {
        peer: p,
        before: baseline,
        after: projected,
        delta,
        ratio_before: p.capacity > 0 ? baseline / p.capacity : 0,
        ratio_after: p.capacity > 0 ? projected / p.capacity : 0,
      };
    }).filter(r => r.before > 0.1 || r.after > 0.1 || r.peer.is_self);
  }, [data, peers, sortedContribs, overridesByEst, explicitOnly]);

  const modifiedEstimateIds = Object.keys(overrides).filter(eid => {
    const o = overrides[eid];
    if (!o) return false;
    return o.selfExplicitPct != null || (o.addedAssignments || []).some(a => a.pct > 0);
  });

  const setSelfPct = (estimateId, pct) => {
    setOverrides(prev => ({
      ...prev,
      [estimateId]: {
        ...(prev[estimateId] || { selfId, addedAssignments: [] }),
        selfId,
        selfExplicitPct: Math.max(0, Math.min(100, Number(pct) || 0)),
      },
    }));
  };

  const setPeerPct = (estimateId, peerId, pct) => {
    setOverrides(prev => {
      const o = prev[estimateId] || { selfId, addedAssignments: [] };
      const others = (o.addedAssignments || []).filter(a => a.resource_id !== peerId);
      const newP = Math.max(0, Math.min(100, Number(pct) || 0));
      const next = newP > 0 ? [...others, { resource_id: peerId, pct: newP }] : others;
      return { ...prev, [estimateId]: { ...o, selfId, addedAssignments: next } };
    });
  };

  const quickShiftToPeer = (estimateId, peerId, contrib) => {
    // Shift 25 percentage-points of effective share FROM self TO peer.
    // Uses each party's CURRENT effective % (explicit OR residual share)
    // as the starting point, so a peer who was already at 50% residual
    // ends up at 75% explicit after the shift — i.e., they truly GAIN
    // 25 points of allocation.
    const SHIFT = 25;
    // Build explicit map snapshot.
    const explicitMap = {};
    for (const a of contrib.current_assignments || []) {
      explicitMap[a.resource_id] = a.explicit_pct;
    }
    const effectiveFor = (rid) => {
      if (rid in explicitMap) return explicitMap[rid];
      if ((contrib.eligible_residual_peer_ids || []).includes(rid)) {
        return contrib.residual_per_peer_pct;
      }
      return 0;
    };

    const prevOverride = overrides[estimateId];
    const curSelfEff = prevOverride?.selfExplicitPct ?? effectiveFor(selfId);
    const prevPeerAdded = (prevOverride?.addedAssignments || []).find(a => a.resource_id === peerId);
    const curPeerEff = prevPeerAdded ? prevPeerAdded.pct : effectiveFor(peerId);

    const shift = Math.min(SHIFT, curSelfEff);
    const newSelf = Math.max(0, curSelfEff - shift);
    const newPeer = curPeerEff + shift;

    setOverrides(prev => {
      const o = prev[estimateId] || { selfId, addedAssignments: [] };
      const others = (o.addedAssignments || []).filter(a => a.resource_id !== peerId);
      return {
        ...prev,
        [estimateId]: {
          ...o,
          selfId,
          selfExplicitPct: newSelf,
          addedAssignments: [...others, { resource_id: peerId, pct: newPeer }],
        },
      };
    });
  };

  const resetEstimate = (estimateId) => {
    setOverrides(prev => {
      const { [estimateId]: _, ...rest } = prev;
      return rest;
    });
  };

  const resetAll = () => setOverrides({});

  const handleSave = async () => {
    if (modifiedEstimateIds.length === 0) {
      toast.info('Nothing to save yet — try adjusting an estimate.');
      return;
    }
    setSaving(true);
    let success = 0;
    let failed = [];
    for (const eid of modifiedEstimateIds) {
      const contrib = sortedContribs.find(c => c.estimate_id === eid);
      if (!contrib) continue;
      const sim = simulateEstimate(contrib, overrides[eid], peers, explicitOnly);
      // Convert explicit map → array, KEEPING the role + other-role
      // existing assignments untouched. We need to fetch the full
      // current assignments and merge with our role-only updates.
      try {
        const cur = await axios.get(
          `${API_URL}/api/estimates/${eid}/resource-assignments`,
          { withCredentials: true },
        );
        const allAssignments = cur.data?.assignments || [];
        const otherRoles = allAssignments.filter(a => a.role !== contrib.role);
        // Send EVERY explicit entry (including 0%) — a 0% explicit value
        // is a meaningful semantic ("this person is off this estimate"
        // i.e. excluded from the residual pool). The backend caps each
        // role's total at 100% and allows 0% entries.
        const sameRoleNew = Object.entries(sim.explicit).map(([rid, pct]) => ({
          resource_id: rid,
          role: contrib.role,
          percentage: pct,
        }));
        await axios.put(
          `${API_URL}/api/estimates/${eid}/resource-assignments`,
          { assignments: [...otherRoles, ...sameRoleNew] },
          { withCredentials: true },
        );
        success += 1;
      } catch (err) {
        failed.push({ eid, name: contrib.estimate_name, err: err?.response?.data?.detail || err?.message });
      }
    }
    setSaving(false);
    if (success > 0) {
      toast.success(`Saved ${success} ${success === 1 ? 'estimate' : 'estimates'}`);
    }
    if (failed.length > 0) {
      toast.error(`Failed to save ${failed.length}: ${failed.map(f => f.name).join(', ')}`);
    } else {
      onSaved && onSaved();
      onClose && onClose();
    }
  };

  const resource = data?.resource;
  const selfCurrent = resource?.current_hours_this_week ?? 0;
  const selfCap = resource?.weekly_capacity_hours ?? 40;
  const selfRow = impactRows.find(r => r.peer.is_self);
  const selfAfter = selfRow?.after ?? selfCurrent;
  const selfDelta = selfAfter - selfCurrent;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose && onClose(); }}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="suggest-reassignment-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-amber-500" />
            Suggest Reassignment
          </DialogTitle>
          <DialogDescription className="sr-only">
            Drill into a single resource × week cell to see contributing
            estimates, lightest same-role peers, and an inline reassignment
            tool with a real-time impact preview.
          </DialogDescription>
        </DialogHeader>

        {loading && (
          <div className="flex items-center justify-center py-12" data-testid="suggest-reassignment-loading">
            <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
          </div>
        )}

        {error && (
          <div className="flex items-start gap-2 text-rose-700 bg-rose-50 p-3 rounded" data-testid="suggest-reassignment-error">
            <AlertTriangle className="w-4 h-4 mt-0.5" />
            <span className="text-sm">{error}</span>
          </div>
        )}

        {data && !loading && (
          <div className="space-y-5">
            {/* Explicit-only toggle — aligns the numbers with the Portfolio Heat-map */}
            <div className="flex items-center justify-between rounded-lg border border-slate-200 bg-white px-3 py-2">
              <div>
                <div className="text-sm font-medium text-slate-800">Explicit assignments only</div>
                <div className="text-[11px] text-slate-500">
                  {explicitOnly
                    ? 'Matching the Portfolio Heat-map — divide-equally residual is excluded.'
                    : 'Includes divide-equally residual across all same-role estimates.'}
                </div>
              </div>
              <Switch
                checked={explicitOnly}
                onCheckedChange={setExplicitOnly}
                data-testid="suggest-reassignment-explicit-only-toggle"
              />
            </div>

            {/* Resource summary */}
            <div className="flex items-start justify-between gap-4 border rounded-lg p-3 bg-slate-50">
              <div>
                <div className="text-xs uppercase tracking-wider text-slate-500">{resource.role_label}</div>
                <div className="font-semibold text-slate-900 text-lg">{resource.name}</div>
                <div className="text-xs text-slate-500">Week of {weekIso}</div>
              </div>
              <div className="text-right">
                <div className="text-xs text-slate-500">Current load</div>
                <div className="flex items-baseline justify-end gap-1.5">
                  <span className={`text-xl font-bold ${loadColor(selfCurrent / selfCap).text}`}>
                    {selfCurrent.toFixed(0)}h
                  </span>
                  <span className="text-xs text-slate-500">/ {selfCap}h</span>
                </div>
                <Badge className={`mt-1 ${loadColor(selfCurrent / selfCap).chip}`}>
                  {pctLabel(selfCurrent / selfCap)}
                </Badge>
                {Math.abs(selfDelta) > 0.1 && (
                  <div className="mt-1 flex items-center justify-end gap-1 text-xs">
                    <ArrowRight className="w-3 h-3 text-slate-400" />
                    <span className={`font-medium ${loadColor(selfAfter / selfCap).text}`}>
                      {selfAfter.toFixed(0)}h · {pctLabel(selfAfter / selfCap)}
                    </span>
                    {selfDelta < 0 ? (
                      <TrendingDown className="w-3 h-3 text-emerald-600" />
                    ) : (
                      <TrendingUp className="w-3 h-3 text-rose-600" />
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Suggested peers chip rail */}
            {suggestedPeers.length > 0 && (
              <div>
                <div className="text-xs uppercase tracking-wider text-slate-500 mb-1.5">
                  Lightest peers this week
                </div>
                <div className="flex flex-wrap gap-2">
                  {suggestedPeers.map(p => (
                    <div
                      key={p.resource_id}
                      className="flex items-center gap-2 border rounded-full px-3 py-1 text-xs bg-emerald-50 border-emerald-200"
                      data-testid={`suggest-reassignment-peer-${p.resource_id}`}
                    >
                      <span className="font-medium text-emerald-900">{p.name}</span>
                      <span className="text-emerald-700">
                        {p.current_hours_this_week.toFixed(0)}h / {p.capacity}h
                        ({pctLabel(p.current_load_ratio)})
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Contributing estimates */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <div className="text-xs uppercase tracking-wider text-slate-500">
                  Contributing estimates ({sortedContribs.length})
                </div>
                {sortedContribs.length > 6 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowAllContribs(v => !v)}
                    data-testid="suggest-reassignment-toggle-show-all"
                  >
                    {showAllContribs ? 'Show top 6' : `Show all ${sortedContribs.length}`}
                  </Button>
                )}
              </div>
              <div className="space-y-2">
                {visibleContribs.map(c => {
                  const o = overrides[c.estimate_id] || {};
                  const isDirty = o.selfExplicitPct != null || (o.addedAssignments || []).some(a => a.pct > 0);
                  return (
                    <div
                      key={c.estimate_id}
                      className={`border rounded-lg p-3 ${isDirty ? 'border-amber-300 bg-amber-50' : 'border-slate-200 bg-white'}`}
                      data-testid={`suggest-reassignment-estimate-${c.estimate_id}`}
                    >
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <div className="min-w-0 flex-1">
                          <div className="font-medium text-slate-800 text-sm flex items-center gap-1.5">
                            {c.estimate_name}
                            <a
                              href={`/workspace/${c.estimate_id}/planner`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-slate-400 hover:text-slate-700"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <ExternalLink className="w-3 h-3" />
                            </a>
                          </div>
                          <div className="text-[11px] text-slate-500 mt-0.5">
                            FTE this week: {c.fte_this_week.toFixed(2)} ·
                            Self hours: <span className="font-semibold">{c.self_hours_this_week.toFixed(1)}h</span> ·
                            {c.self_is_explicit
                              ? ` Explicit ${c.self_explicit_pct.toFixed(0)}%`
                              : ` Residual share ${c.residual_per_peer_pct.toFixed(0)}%`}
                          </div>
                        </div>
                        {isDirty && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => resetEstimate(c.estimate_id)}
                            data-testid={`suggest-reassignment-reset-${c.estimate_id}`}
                          >
                            <RotateCcw className="w-3 h-3 mr-1" /> Reset
                          </Button>
                        )}
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        {/* Self explicit % */}
                        <label className="flex items-center gap-2 text-xs">
                          <span className="text-slate-600 w-28 shrink-0">Self explicit %</span>
                          <Input
                            type="number"
                            min={0}
                            max={100}
                            step={5}
                            value={
                              o.selfExplicitPct != null
                                ? o.selfExplicitPct
                                : (c.self_is_explicit
                                    ? c.self_explicit_pct
                                    : Math.round(c.residual_per_peer_pct))
                            }
                            placeholder={c.self_is_explicit ? '' : `${Math.round(c.residual_per_peer_pct)} (residual)`}
                            onChange={(e) => setSelfPct(c.estimate_id, e.target.value)}
                            className="h-8 text-xs"
                            data-testid={`suggest-reassignment-self-pct-${c.estimate_id}`}
                          />
                        </label>

                        {/* Quick-shift to suggested peers */}
                        {suggestedPeers.length > 0 && (
                          <div className="flex flex-wrap items-center gap-1">
                            <span className="text-[11px] text-slate-500 mr-1">Quick shift →</span>
                            {suggestedPeers.map(p => (
                              <Button
                                key={p.resource_id}
                                variant="outline"
                                size="sm"
                                className="text-[11px] h-7"
                                onClick={() => quickShiftToPeer(c.estimate_id, p.resource_id, c)}
                                data-testid={`suggest-reassignment-quickshift-${c.estimate_id}-${p.resource_id}`}
                              >
                                +25% {p.name.split(' ')[0]}
                              </Button>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* Show added assignments */}
                      {(o.addedAssignments || []).map(a => {
                        const peer = peers.find(p => p.resource_id === a.resource_id);
                        if (!peer) return null;
                        return (
                          <div
                            key={a.resource_id}
                            className="mt-2 flex items-center gap-2 text-xs bg-white border border-emerald-200 rounded p-2"
                          >
                            <span className="font-medium text-emerald-900">{peer.name}</span>
                            <Input
                              type="number"
                              min={0}
                              max={100}
                              step={5}
                              value={a.pct}
                              onChange={(e) => setPeerPct(c.estimate_id, a.resource_id, e.target.value)}
                              className="h-7 text-xs w-20"
                              data-testid={`suggest-reassignment-peer-pct-${c.estimate_id}-${a.resource_id}`}
                            />
                            <span className="text-slate-500">%</span>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setPeerPct(c.estimate_id, a.resource_id, 0)}
                              className="ml-auto h-7 px-2"
                            >
                              <X className="w-3 h-3" />
                            </Button>
                          </div>
                        );
                      })}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Real-time impact preview */}
            {modifiedEstimateIds.length > 0 && (
              <div data-testid="suggest-reassignment-impact-preview">
                <div className="text-xs uppercase tracking-wider text-slate-500 mb-1.5">
                  Impact preview · {modifiedEstimateIds.length} {modifiedEstimateIds.length === 1 ? 'estimate' : 'estimates'} pending
                </div>
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full text-xs">
                    <thead className="bg-slate-50 text-slate-600">
                      <tr>
                        <th className="text-left px-3 py-2 font-medium">Resource</th>
                        <th className="text-right px-3 py-2 font-medium">Before</th>
                        <th className="text-right px-3 py-2 font-medium">After</th>
                        <th className="text-right px-3 py-2 font-medium">Δ</th>
                      </tr>
                    </thead>
                    <tbody>
                      {impactRows.map(r => {
                        const colBefore = loadColor(r.ratio_before);
                        const colAfter = loadColor(r.ratio_after);
                        const changed = Math.abs(r.delta) > 0.1;
                        return (
                          <tr key={r.peer.resource_id} className={changed ? 'bg-amber-50/50' : ''}>
                            <td className="px-3 py-1.5 font-medium text-slate-800">
                              {r.peer.name}{r.peer.is_self ? ' (self)' : ''}
                              {r.peer.blocked_this_week && <span className="ml-1 text-[10px] text-slate-500">PTO</span>}
                            </td>
                            <td className="text-right px-3 py-1.5">
                              <span className={colBefore.text}>{r.before.toFixed(0)}h</span>
                              <span className="text-slate-400 ml-1">({pctLabel(r.ratio_before)})</span>
                            </td>
                            <td className="text-right px-3 py-1.5">
                              <span className={`font-semibold ${colAfter.text}`}>{r.after.toFixed(0)}h</span>
                              <span className="text-slate-400 ml-1">({pctLabel(r.ratio_after)})</span>
                            </td>
                            <td className={`text-right px-3 py-1.5 font-medium ${r.delta < 0 ? 'text-emerald-700' : r.delta > 0 ? 'text-rose-700' : 'text-slate-400'}`}>
                              {r.delta === 0 ? '—' : (r.delta > 0 ? `+${r.delta.toFixed(0)}h` : `${r.delta.toFixed(0)}h`)}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        <DialogFooter className="gap-2">
          <Button
            variant="ghost"
            onClick={resetAll}
            disabled={modifiedEstimateIds.length === 0 || saving}
            data-testid="suggest-reassignment-reset-all-btn"
          >
            Reset all
          </Button>
          <Button
            variant="outline"
            onClick={onClose}
            disabled={saving}
            data-testid="suggest-reassignment-cancel-btn"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={modifiedEstimateIds.length === 0 || saving}
            data-testid="suggest-reassignment-save-btn"
          >
            {saving ? (
              <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Saving…</>
            ) : (
              <><Save className="w-4 h-4 mr-1.5" /> Save {modifiedEstimateIds.length || ''}</>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
